import { useTranslation } from "react-i18next";
import { Box, Chip, Typography } from "@mui/material";

import { formatImgUrl } from "../../../../../utils/cardFormating";
import { formatLocalizedDate } from "../../../../../utils/formatDate";

import { CandidateDetails } from "../../../types/candidate";
import CertificateIcon from "../../../assets/images/certificate-figure.png";
import { NoIcon, YesIcon } from "../../../assets/icons/candidate-details-page-icons";

const ApplicantFormTab = ({ candidateDetails }: { candidateDetails: CandidateDetails }) => {
  const { t, i18n } = useTranslation("goHire", { keyPrefix: "candidateDetailsPage.applicantFormTab" });
  const { t: tLanguage } = useTranslation("goHire", { keyPrefix: "enum.language" });

  const { languagesSpoken, willingnessToRelocate, introductionVideo, clubsAcademiesWorkedIn, certificateAndLicenses } =
    candidateDetails;
  return (
    <Box sx={styles.container}>
      {languagesSpoken && (
        <Box component="fieldset" sx={styles.dataContainer}>
          <Typography component="label" sx={styles.dataTitle}>
            {t("languagesSpoken")}
          </Typography>
          <Box sx={styles.dataChipContainer}>
            {languagesSpoken.map((language) => (
              <Chip key={language} label={tLanguage(language)} sx={styles.dataChipValue} />
            ))}
          </Box>
        </Box>
      )}
      {willingnessToRelocate && (
        <Box component="fieldset" sx={styles.dataContainer}>
          <Typography component="label" sx={styles.dataTitle}>
            {t("willingnessToRelocate")}
          </Typography>
          <Box sx={styles.dataChipContainer}>
            {Boolean(willingnessToRelocate) ? (
              <Box sx={styles.booleanDataContainer}>
                <YesIcon />
                <Typography sx={styles.booleanDataValue}>{t("yes")}</Typography>
              </Box>
            ) : (
              <Box sx={styles.booleanDataContainer}>
                <NoIcon />
                <Typography sx={styles.booleanDataValue}>{t("no")}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {introductionVideo && (
        <Box component="fieldset" sx={styles.dataContainer}>
          <Typography component="label" sx={styles.dataTitle}>
            {t("introductionVideo")}
          </Typography>
          <Box component="video" src={formatImgUrl(introductionVideo.url)} controls sx={styles.video} />
        </Box>
      )}
      {clubsAcademiesWorkedIn && (
        <Box component="fieldset" sx={styles.dataContainer}>
          <Typography component="label" sx={styles.dataTitle}>
            {t("specifyClubsAcademiesWorkedIn")}
          </Typography>
          <Box sx={styles.dataChipContainer}>
            {clubsAcademiesWorkedIn.map((club) => (
              <Chip key={club} label={club} sx={styles.dataChipValue} />
            ))}
          </Box>
        </Box>
      )}
      {certificateAndLicenses && (
        <Box component="fieldset" sx={styles.dataContainer}>
          <Typography component="label" sx={styles.dataTitle}>
            {t("certificateAndLicenses")}
          </Typography>
          <Box sx={styles.certificatesContainer}>
            {certificateAndLicenses.map((certificate) => (
              <Box sx={styles.certificateContainer}>
                <Box component="img" src={CertificateIcon} sx={styles.certificateIcon} />
                <Box sx={styles.certificateContent}>
                  <Typography sx={styles.certificateTitle}>{certificate.title}</Typography>
                  {/* <Typography sx={styles.certificateDate}>
                    {certificate.startDate ? formatLocalizedDate(certificate.startDate, i18n.language) : "--"}
                    {" • "}
                    {certificate.endDate ? formatLocalizedDate(certificate.endDate, i18n.language) : "--"}
                  </Typography> */}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ApplicantFormTab;

const styles = {
  container: {
    display: "flex",
    padding: { xs: "12px", sm: "32px" },
    flexDirection: "column",
    gap: "32px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #EAECF0)",
    background: "var(--White, #FFF)",
  },
  dataContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    border: "none",
  },
  dataTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  dataChipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  },
  dataChipValue: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    height: "fit-content",
    "& .MuiChip-label": {
      borderRadius: "8px",
      border: "1px solid var(--Gray-300, #D0D5DD)",
      padding: "4px 10px",
      margin: "0",
    },
  },
  booleanDataContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  booleanDataValue: {
    fontFamily: "Montserrat",
    color: "var(--Dark-Mode---100, #1E2021)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
  },
  video: {
    width: "100%",
    maxWidth: "805px",
    aspectRatio: "16/9",
    objectFit: "contain",
    borderRadius: "12px",
    background: "#000",
  },
  certificatesContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    gap: "16px",
    flexWrap: "wrap",
  },
  certificateContainer: {
    display: "flex",
    gap: "16px",
    width: { xs: "100%", sm: "calc(50% - 8px)" },
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Base-White, #FFF)",
  },
  certificateIcon: {
    width: "43px",
    height: "52px",
  },
  certificateContent: {
    display: "flex",
    flexDirection: "column",
  },
  certificateTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  certificateDate: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
  },
};
